/*
 * Copyright (c) Nexign, JSC, 2025.
 */
/**
 *
 *
 * @author Julia Ignatieva
 * @version 1.37.0
 * @since 1.37.0
 */

import { Fragment, useCallback } from 'react';

import { connect } from 'react-redux';
import { useParams } from 'react-router-dom';
import { Dispatch } from 'redux';
import { createStructuredSelector } from 'reselect';


import { EBasketType } from '@modules/app/appConstants';
import { TranslationFileNamesEnum } from '@modules/internationalization/constants';
import {
    makeSelectTranslationWizardChangeRole,
} from '@modules/internationalization/intSelectors';
import { ECommonTranslationPages } from '@modules/internationalization/types/common/pages';
import { IWizardChangeRoleTranslation } from '@modules/internationalization/types/wizards/changeRole';
import { EWizardName, WIZARD_ALL_ENTITIES } from '@modules/wizard/wizardConstants';
import { IWizardState } from '@modules/wizard/wizardReducer';
import { makeSelectWizardStepName } from '@modules/wizard/wizardSelectors';
import { IRootReducer } from '@reducers/rootReducer';
import { IRouteParams } from '@routes/constants/routesConstants';

import Title from '../../../components/common/Title/Title';
import withAsyncStateManagement from '../../../modules/asyncStateManagment/WithAsyncStateManagement';
import PageLocale from '../../../modules/internationalization/components/PageLocale';
import Wizard from '../../../modules/wizard/components/Wizard';
import { wizardChangeRoleHandleNextAction } from '../wizardChangeRoleActions';
import { EWizardChangeRoleSteps } from '../wizardChangeRoleConstant';
import wizardChangeRoleReducer, { IWizardChangeRoleReducer } from '../wizardChangeRoleReducer';
import { makeSelectWizardChangeRoleValidation } from '../wizardChangeRoleSelectors';
import WizardChangeRoleConfirmation from './WizardChangeRoleConfirmation';
import WizardChangeRoleSelectParams from './WizardChangeRoleSelectParams';

const WizardChangeRole = (props: IConnectedProps & IDispatchProps) => {

    const {
        stepName,
        translationChangeRole,
        validation,

        handleNext,
    } = props;

    const { subscriberId } = useParams<IRouteParams>();

    const renderStep = useCallback(() => {
        switch (stepName) {
            case EWizardChangeRoleSteps.StepSelectParams:
                return <WizardChangeRoleSelectParams/>;
            case EWizardChangeRoleSteps.StepConfirmation:
                return <WizardChangeRoleConfirmation/>;
        }
    }, [stepName]);

    return (
        <Fragment>
            <PageLocale lazy
                        fileNames={[
                            TranslationFileNamesEnum.wizardChangeRole,
                            TranslationFileNamesEnum.wizard,
                        ]}/>

            <Title pageName={ECommonTranslationPages.wizardChangeRole}/>

            <Wizard
                basketType={EBasketType.MOBILE}
                entityId={subscriberId ? parseInt(subscriberId) : WIZARD_ALL_ENTITIES}
                isNextDisabled={!validation?.isValid}
                name={EWizardName.CHANGE_ROLE}
                translationProps={{
                    error: translationChangeRole?.error,
                    steps: translationChangeRole?.steps,
                    title: translationChangeRole?.title,
                }}
                validateError={validation?.error}
                onWizardNextHandler={handleNext}
            >
                {renderStep()}
            </Wizard>

        </Fragment>
    );
};

interface IConnectedProps extends Pick<IWizardState, 'stepName'>,
    Pick<IWizardChangeRoleReducer, 'validation'> {
    translationChangeRole: IWizardChangeRoleTranslation;
}

const mapStateToProps = createStructuredSelector<IRootReducer, IConnectedProps>({
    stepName: makeSelectWizardStepName(),
    translationChangeRole: makeSelectTranslationWizardChangeRole(),
    validation: makeSelectWizardChangeRoleValidation(),
});

export interface IDispatchProps {
    handleNext: () => void;
}

export const mapDispatchToProps = (dispatch: Dispatch): IDispatchProps => ({
    handleNext: () => dispatch(wizardChangeRoleHandleNextAction()),
});

export default withAsyncStateManagement(
    connect(mapStateToProps, mapDispatchToProps)(WizardChangeRole),
    { reducer: { wizardChangeRole: wizardChangeRoleReducer } },
);
